import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Crown, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function UpgradeSuccess() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        if (attempts >= 6) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 6) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    // Runs once on mount (guarded by `started`); refreshUser is stable via useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="upgrade-success-page" className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-md border border-border bg-card p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-5 font-display text-3xl tracking-wider">Confirming payment…</h1>
            <p className="mt-2 text-foreground/60">Hold tight while the Entity processes your tribute.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center border border-primary/40 bg-primary/10"><Crown className="h-8 w-8 text-primary" /></div>
            <h1 className="mt-5 font-display text-4xl tracking-wider text-primary">Welcome to Pro!</h1>
            <p className="mt-2 text-foreground/60">Unlimited generations, analytics and custom challenges are unlocked.</p>
            <Button asChild className="mt-6 rounded-sm bg-primary font-bold uppercase tracking-widest"><Link to="/dashboard">Go to Dashboard</Link></Button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-5 font-display text-3xl tracking-wider">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="mt-2 text-foreground/60">{state === "timeout" ? "It's taking longer than expected. Check your dashboard shortly." : "Something went wrong. You can try again."}</p>
            <Button asChild variant="outline" className="mt-6 rounded-sm border-white/10 bg-transparent uppercase tracking-widest"><Link to="/pricing">Back to Pricing</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
